import type { PopupSquareConfig } from '@coorum-research/shared-common/types/popup.type'
import type { CSSProperties, MouseEvent, RefObject } from 'react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { getPublicCloudflareVideoThumbnailUrl } from '../utils'
import { CloseButton } from './CloseButton'
import { MediaItem } from './MediaItem'

type Props = {
  configJson?: PopupSquareConfig | null
  onClick?: () => void
  onClose?: () => void
  isEmbed?: boolean
  ref?: RefObject<HTMLDivElement | null>
}

export function LauncherPopupSquare({ ref, configJson, onClick, onClose, isEmbed }: Props) {
  const {
    title,
    description,
    horizontal,
    vertical,
    displayPosition,
    imageUrl,
    imageType,
    mediaFileType,
    videoUrl,
    videoUid,
    theme,
  } = configJson ?? {}

  const containerRef = useRef<HTMLDivElement | null>(null)
  const [closePositionSet, setClosePositionSet] = useState<Pick<CSSProperties, 'bottom' | 'left' | 'right'> | null>(null)

  const displayPositionSet = useMemo(() => {
    if (displayPosition === 'bottom-right') {
      return {
        bottom: `${vertical}px`,
        right: `${horizontal}px`,
      }
    } else if (displayPosition === 'bottom-left') {
      return {
        bottom: `${vertical}px`,
        left: `${horizontal}px`,
      }
    }
  }, [displayPosition, horizontal, vertical])

  // 右下、左下の値を連動して、.cr-research-launcherを bottom 0, right 0 または bottom 0, left 0 になるようにする
  useEffect(() => {
    // window.parent.document.getElementById('cr-research-launcher')
    const launcherEl = window.parent.document.getElementById('cr-research-launcher') || window.document.querySelector('.cr-research-launcher')
    if (!launcherEl) {
      return
    }

    if (displayPosition === 'bottom-right') {
      launcherEl?.style.setProperty('bottom', '0')
      launcherEl?.style.setProperty('right', '0')
    } else if (displayPosition === 'bottom-left') {
      launcherEl?.style.setProperty('bottom', '0')
      launcherEl?.style.setProperty('left', '0')
    }
  }, [displayPosition, displayPositionSet])

  useEffect(() => {
    if (!containerRef?.current) {
      return
    }

    const closeBtnMargin = 24 / 2
    const v = vertical ?? 0
    const h = horizontal ?? 0
    const width = containerRef.current.clientWidth
    const height = containerRef.current.clientHeight

    if (displayPosition === 'bottom-right') {
      setClosePositionSet({
        bottom: `${v + height - closeBtnMargin}px`,
        right: `${h - closeBtnMargin}px`,
      })
    } else if (displayPosition === 'bottom-left') {
      setClosePositionSet({
        bottom: `${v + height - closeBtnMargin}px`,
        left: `${h + width - closeBtnMargin}px`,
      })
    }
  }, [title, description, vertical, horizontal, displayPosition, imageUrl, imageType, containerRef])

  const handleClose = (e: MouseEvent) => {
    e.stopPropagation()

    if (onClose) {
      onClose()
    }
  }

  const isMediaExist = useMemo(() => {
    return imageUrl || videoUrl
  }, [imageUrl, videoUrl])

  return (
    <>
      <div
        ref={(node) => {
          containerRef.current = node
          if (ref) {
            ref.current = node
          }
        }}
        onClick={onClick}
        style={{
          ...displayPositionSet,
          cursor: 'pointer',
          position: isEmbed ? undefined : 'absolute',
          overflow: 'hidden',
          width: 270,
          height: 'auto',
          backgroundColor: '#ffffff',
          borderWidth: '1px',
          borderColor: theme?.mainColor,
          filter: 'drop-shadow(0 4px 3px rgb(0 0 0 / 0.07)) drop-shadow(0 2px 2px rgb(0 0 0 / 0.06))',
          fontSize: '16px',
          borderRadius: '0.5em',
        }}
      >
        {(title || isMediaExist) && (
          <div
            style={{
              height: isMediaExist && description ? 'auto' : '100%',
              minHeight: isMediaExist && description ? '33%' : '100%',
              position: 'relative',
              backgroundColor: theme?.mainColor,
              whiteSpace: 'pre-wrap',
            }}
          >
            {isMediaExist && (
              <div
                style={{
                  position: 'relative',
                  height: '100%',
                }}
              >
                {imageType === 'titleBg' && (
                  <>
                    {/* This empty <div> is used for spacing or layout purposes. It ensures consistent height and padding for the title background. */}
                    <div
                      style={{
                        width: '100%',
                        height: 133,
                        color: theme?.mainTextColor,
                        padding: '0.5em 1em',
                        fontWeight: 500,
                      }}
                    />
                    <MediaItem
                      mediaFileType={mediaFileType}
                      videoUrl={videoUrl}
                      videoThumbnailUrl={getPublicCloudflareVideoThumbnailUrl(videoUid)}
                      imageUrl={imageUrl}
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                      }}
                      alt={`${title} - research main title background`}
                    />
                  </>
                )}
                {imageType === 'icon' && (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      overflow: 'hidden',
                    }}
                  >
                    <p
                      style={{
                        flexGrow: 1,
                        padding: '0.5em 1em',
                        fontWeight: 500,
                        color: theme?.mainTextColor,
                      }}
                    >
                      {title ?? ''}
                    </p>
                    <MediaItem
                      mediaFileType={mediaFileType}
                      videoUrl={videoUrl}
                      imageUrl={imageUrl}
                      style={{
                        flexShrink: 0,
                        marginRight: '1em',
                        marginTop: '0.5em',
                        marginBottom: '0.5em',
                        width: '3.5em',
                        height: '3.5em',
                        objectFit: 'cover',
                        borderRadius: '50%',
                      }}
                      alt={`${title} - research main icon`}
                    />
                  </div>
                )}
              </div>
            )}
            {!isMediaExist && (
              <p
                style={{
                  padding: '0.5em 1em',
                  fontWeight: 500,
                  color: theme?.mainTextColor,
                }}
              >
                {title}
              </p>
            )}
          </div>
        )}
        {description && (
          <div
            style={{
              padding: '0.5em 1em',
              color: theme?.bodyTextColor || theme?.mainTextColor,
            }}
          >
            <p style={{ whiteSpace: 'pre-wrap' }}>
              {description}
            </p>
          </div>
        )}
      </div>
      {configJson?.showCloseButton && onClose && closePositionSet
        && <CloseButton theme="black" style={{ position: 'absolute', ...closePositionSet }} onClick={handleClose} />}
    </>
  )
}
